import React, { useState } from 'react';
import BottomSheet from './BottomSheet';
import {
	sectionPreview,
	lyricsTextOf,
	splitSectionAt,
	mergeWithPrevious,
	sectionLines,
} from '../../../../services/songSectionsModel';
import './SectionsSheet.css';

const SOURCE_LINE = {
	lyrics: 'Taken from the headings in the lyrics.',
	auto: 'Labelled automatically from the lyrics. Check them against the recording and fix anything that is off.',
	heuristic: 'A best guess from repeated verses, made without the full lyrics analysis. Check them, or detect again.',
	edited: 'Set by a teacher.',
};

/**
 * Song sections sheet (2026-09-25; split and merge 2026-09-26): rename a
 * section, split one at a lyric line, merge one into the section before it,
 * or detect again. Every change is a DRAFT until "Save sections", which stores
 * the set as 'edited' so it is kept on every later open; "Detect again"
 * replaces it and says so first. A song with no lyrics shows the generic set
 * and explains how to get real ones rather than offering edits with nowhere
 * to be saved.
 */
const SectionsSheet = ({ song, sections, isGeneric, status, canDetect, onRename, onDetectAgain, onClose }) => {
	const [draft, setDraft] = useState(sections);
	const [splitting, setSplitting] = useState(null); // section id showing its lines
	const [confirmDetect, setConfirmDetect] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');
	const text = lyricsTextOf(song?.lyrics);
	const meta = song?.sectionsMeta || {};
	const source = meta.source;
	const clean = (list) => list.map((s) => ({ ...s, label: (s.label || '').trim() }));
	const changed = JSON.stringify(clean(draft)) !== JSON.stringify(clean(sections));
	const hasBlank = draft.some((s) => !(s.label || '').trim());
	const detecting = status === 'detecting';

	const rename = (id, label) => setDraft((d) => d.map((s) => (s.id === id ? { ...s, label } : s)));

	const save = async () => {
		if (!changed || hasBlank || saving) return;
		setSaving(true);
		setError('');
		const ok = await onRename(clean(draft));
		setSaving(false);
		if (ok) onClose();
		else setError('The sections did not save. Try again.');
	};

	const detect = async () => {
		setConfirmDetect(false);
		setError('');
		const ok = await onDetectAgain();
		if (!ok) setError('Could not detect sections just now. Try again in a moment.');
		else onClose();
	};

	return (
		<BottomSheet
			title="Song sections"
			titleId="ld-sections-title"
			className="ld-sectionssheet"
			onClose={onClose}
			footer={
				!isGeneric && (
					<div className="ld-sectionssheet__foot">
						<button
							type="button"
							className="ld-sectionssheet__btn ld-sectionssheet__btn--primary"
							onClick={save}
							disabled={!changed || hasBlank || saving}>
							<i className="fa-sharp fa-solid fa-check" aria-hidden="true" />
							{saving ? 'Saving' : 'Save sections'}
						</button>
						<button type="button" className="ld-sectionssheet__btn" onClick={onClose}>
							{changed ? 'Discard' : 'Done'}
						</button>
					</div>
				)
			}>
			{isGeneric ? (
				<p className="ld-sectionssheet__intro">
					{detecting
						? 'Finding sections from the lyrics.'
						: text.trim()
							? 'These are general sections for now. Detect them from the lyrics to get this song’s own sections.'
							: 'This song has no lyrics yet, so these are general sections. Add the lyrics (the Lyrics button) and the song’s own sections are found from them.'}
				</p>
			) : (
				<>
					{source && <p className="ld-sectionssheet__intro">{SOURCE_LINE[source] || ''}</p>}
					{source === 'auto' && meta.recognized === false && (
						<p className="ld-sectionssheet__intro">
							This recording was not recognised, so the sections come from the lyrics alone.
						</p>
					)}
					<ol className="ld-sectionssheet__list">
						{draft.map((s, i) => {
							const lines = splitting === s.id ? sectionLines(text, draft, s.id) : [];
							return (
								<li key={s.id} className="ld-sectionssheet__item">
									<input
										className="ld-sectionssheet__name"
										aria-label={`Name for section ${i + 1}`}
										value={s.label}
										maxLength={40}
										onChange={(e) => rename(s.id, e.target.value)}
									/>
									<span className="ld-sectionssheet__preview">{sectionPreview(text, s)}</span>
									<div className="ld-sectionssheet__row">
										<button
											type="button"
											className="ld-sectionssheet__btn ld-sectionssheet__btn--small"
											aria-expanded={splitting === s.id}
											onClick={() => setSplitting((cur) => (cur === s.id ? null : s.id))}>
											<i className="fa-sharp fa-solid fa-scissors" aria-hidden="true" />
											Split
										</button>
										{i > 0 && (
											<button
												type="button"
												className="ld-sectionssheet__btn ld-sectionssheet__btn--small"
												onClick={() => {
													setSplitting(null);
													setDraft((d) => mergeWithPrevious(d, s.id));
												}}>
												<i className="fa-sharp fa-solid fa-arrow-up" aria-hidden="true" />
												Merge into previous
											</button>
										)}
									</div>
									{splitting === s.id && (
										<div className="ld-sectionssheet__lines" role="group" aria-label="Split before a line">
											{lines.length < 2 ? (
												<p className="ld-sectionssheet__hint">This section is one line; there is nowhere to split it.</p>
											) : (
												<>
													<p className="ld-sectionssheet__hint">Tap the line the new section starts on.</p>
													{lines.slice(1).map((l) => (
														<button
															key={l.line}
															type="button"
															className="ld-sectionssheet__line"
															onClick={() => {
																setDraft((d) => splitSectionAt(d, s.id, l.line));
																setSplitting(null);
															}}>
															{l.text}
														</button>
													))}
												</>
											)}
										</div>
									)}
								</li>
							);
						})}
					</ol>
					{hasBlank && <p className="ld-sectionssheet__error">Every section needs a name.</p>}
				</>
			)}

			{error && <p className="ld-sectionssheet__error" role="alert">{error}</p>}

			{canDetect && (
				<div className="ld-sectionssheet__detect">
					{confirmDetect ? (
						<>
							<p className="ld-sectionssheet__warn">
								{source === 'edited'
									? 'Detecting again replaces the sections a teacher set.'
									: 'Detecting again replaces these sections.'}{' '}
								Notes already filed stay where they are.
							</p>
							<div className="ld-sectionssheet__row">
								<button type="button" className="ld-sectionssheet__btn" onClick={() => setConfirmDetect(false)}>
									Keep these
								</button>
								<button type="button" className="ld-sectionssheet__btn ld-sectionssheet__btn--primary" onClick={detect}>
									Detect again
								</button>
							</div>
						</>
					) : (
						<button
							type="button"
							className="ld-sectionssheet__btn"
							disabled={detecting}
							onClick={() => (isGeneric ? detect() : setConfirmDetect(true))}>
							<i className={`fa-sharp fa-solid ${detecting ? 'fa-rotate fa-spin' : 'fa-wand-magic-sparkles'}`} aria-hidden="true" />
							{detecting ? 'Finding sections' : isGeneric ? 'Detect from the lyrics' : 'Detect again'}
						</button>
					)}
				</div>
			)}
		</BottomSheet>
	);
};

export default SectionsSheet;
